import React from 'react';
import { Box, Grid, Paper, Skeleton, Stack } from '@mui/material';

/**
 * Loading placeholders that mirror the real page layouts so the frame of the
 * page renders instantly and data fills in behind it.
 */

/** One row matching the Find a Loan Officer list cards (100px image + text). */
export function ListRowSkeleton() {
  return (
    <Paper sx={{ p: 3, width: '100%', display: 'flex', alignItems: 'center', mb: 2, borderRadius: 3 }}>
      <Skeleton variant="rounded" width={100} height={100} sx={{ mr: 3, flex: '0 0 auto' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="text" width="25%" />
        <Skeleton variant="text" width="30%" />
      </Box>
    </Paper>
  );
}

export function ListRowSkeletons({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <ListRowSkeleton key={i} />
      ))}
    </>
  );
}

/** Loan officer profile hero: round photo, name/title/contact lines, two buttons, bio. */
export function ProfileHeroSkeleton() {
  return (
    <Box sx={{ pt: '84px', px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 3,
          mb: 4,
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flex: 1,
            minWidth: 0,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Skeleton variant="circular" width={220} height={220} sx={{ flex: '0 0 auto' }} />
          <Box sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: 420 }}>
            <Skeleton variant="text" width="70%" height={48} />
            <Skeleton variant="text" width="45%" height={32} />
            <Skeleton variant="text" width="35%" />
            <Skeleton variant="text" width="50%" sx={{ mt: 1 }} />
            <Skeleton variant="text" width="60%" />
          </Box>
        </Box>
        <Stack spacing={2} sx={{ width: { xs: '100%', sm: 220 }, alignItems: 'center' }}>
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
        </Stack>
      </Box>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="95%" />
      <Skeleton variant="text" width="80%" />
    </Box>
  );
}

/** Branch page: full-width hero banner plus a grid of team cards. */
export function BranchPageSkeleton() {
  return (
    <>
      <Skeleton variant="rectangular" width="100%" sx={{ height: { xs: 300, md: 450 }, mb: 6 }} />
      <Box sx={{ mt: 4, mb: 6 }}>
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="85%" />
      </Box>
      <Skeleton variant="text" width={240} height={40} sx={{ mb: 1 }} />
      <Grid container spacing={4}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper sx={{ p: 3 }}>
              <Stack alignItems="center" spacing={2}>
                <Skeleton variant="circular" width={80} height={80} />
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="45%" />
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="text" width="70%" />
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
